import styled, { css } from 'styled-components'
import { breakpoint } from 'utils'

import { Box, Text } from 'components/Box'
import { TruncateWithTooltip } from 'components/Truncate'
import { ButtonUnstyled } from 'components/Button'
import Tooltip from 'components/Tooltip'
import Caption from 'components/Caption'
import LinkUnstyled from 'components/LinkUnstyled'
import { linkStyles } from 'styles/global'

import { ReactComponent as CheckIcon } from 'icons/check-16.svg'
import { ReactComponent as ExclamationMarkIcon } from 'icons/exclamation-mark-circle-11.svg'
import { ReactComponent as ArrowDownCircle } from 'icons/arrow-down-circle-16.svg'

// Notification structure
export const NotificationContainer = styled(LinkUnstyled)<{
  $isRead?: boolean
}>(
  ({ theme, $isRead }) => css`
    display: flex;
    align-items: flex-start;

    padding: 16px;

    margin-left: ${!$isRead ? '24px' : undefined};

    white-space: pre-wrap;
    color: inherit;

    position: relative;
    transition: background ${theme.transitions.duration[1]}
      ${theme.transitions.timing.easeOut};

    &:nth-child(even) {
      background-color: ${theme.colors.background.secondary[2]};
    }

    ${breakpoint('xs')`
      margin-left: ${!$isRead ? '24px' : undefined};
      padding: 16px 16px 16px 0;
      border-radius: ${theme.radii.xlarge};
    `}

    /* stylelint-disable-next-line no-descending-specificity */
    &:hover,
    [data-testid='notifications-read-title'] ~ &:hover {
      background: ${theme.colors.state.hover};

      ${MarkAsReadButtonContainer} {
        opacity: 1;
      }
    }
  `
)

export const NotificationFlex = styled.div`
  display: flex;
  align-items: center;

  flex: 1;

  max-width: 100%;
`

export const NotificationItemContainer = styled.div`
  min-width: 0;
  margin-left: ${({ theme }) => theme.space[4]}px;
  display: flex;
  flex: 1;
  flex-direction: column;
`

export const NotificationShareName = styled(TruncateWithTooltip)(
  ({ theme }) => css`
    font-size: ${theme.fontSizes.little};
    color: ${theme.colors.foreground.secondary[4]};
  `
)

export const NotificationDescription = styled(Text)(
  ({ theme }) => css`
    margin: 0;
    font-size: ${theme.fontSizes.small};
    font-weight: ${theme.fontWeights.regular};
    line-height: 1.25;
    overflow-wrap: 'break-word';
  `
)

export const NotificationBody = styled(Box).attrs({
  mt: 1,
  mb: 0,
})(
  ({ theme }) => css`
    display: flex;
    flex: 1;

    align-items: flex-start;

    font-weight: ${theme.fontWeights.regular};
    font-size: ${theme.fontSizes.small};
    color: ${theme.colors.foreground.secondary[2]};
    word-break: break-all;
  `
)

export const NotificationsMeta = styled(Caption).attrs({
  mt: 1,
  mb: 0,
  ml: 2,
})(
  ({ theme }) => css`
    display: block;
    color: ${theme.colors.text.dark.secondary};
    font-size: ${theme.fontSizes.smaller};
  `
)

// Other styles
export const UnreadIndicator = styled.span`
  &:before {
    content: '';
    position: absolute;
    top: 50%;
    left: 8px;
    background: ${({ theme }) => theme.colors.primaryTint[100]};
    width: 8px;
    height: 8px;
    margin-top: -4px;
    border-radius: 50%;
  }

  ${breakpoint('xs')`
    &:before {
      left: -16px;
    }
  `}

  ${breakpoint('md')`
    &:before {
      left: -24px;
    }
  `}

  &:hover {
    background: ${({ theme }) => theme.colors.background.light.secondary};
  }
`

export const FailedAsset = styled.div`
  color: ${({ theme }) => theme.colors.red};
`

export const TooltipPopup = styled.div`
  width: 70px;
`

export const MarkAsReadButtonContainer = styled.div`
  margin-left: 8px;

  ${breakpoint('md')`
    width: 50px;
    display: flex;
    justify-content: flex-end;
    margin-left: 0;
  `}

  @media (hover: hover) {
    opacity: 0;
  }
`

export const MarkAsReadIcon = styled(CheckIcon)`
  width: 16px;
  height: 16px;
  margin: -8px;
  flex: 0 0 auto;
`

export const FailedIcon = styled(ExclamationMarkIcon)`
  width: 11px;
  height: 11px;
  flex: 0 0 auto;
`

export const AssetReadyIcon = styled(ArrowDownCircle)`
  width: 16px;
  height: 16px;
  margin-right: 4px;
  margin-top: 2px; /* stylelint-disable-line scales/space */
  flex: 0 0 auto;
  color: ${({ theme }) => theme.colors.foreground.secondary[4]};
`

export const TooltipAvatar = styled(Tooltip)`
  display: flex;
`

export const DownloadAsset = styled(ButtonUnstyled)(
  ({ theme }) => css`
    ${linkStyles};
    text-decoration: underline;
    font-weight: ${theme.fontWeights.semibold};
  `
)
